import {FlagProps} from 'platform/components';
import {ThemeIconKey} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {TelephonyCallDetailRecord} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {
  getBusinessCaseStateFlag,
  getBusinessCaseTypeFlag,
  getInterestStateFlagProps,
  getInterestTypeFlagProps,
  RECORD_AUDIT_PAIR_RESOURCE_ICONS,
} from '@dms/shared';

import {Nullish} from 'shared/web';

interface CallLinkedRecordContent {
  /** The record kind, as the sidebar names it. */
  kindLabel: string;
  /** The record kind's icon, as the sidebar shows it. */
  icon: ThemeIconKey;
  /** The record's number; null when the viewer may not read it. */
  code: string | null;
  /** Type and state of the record, in that order. */
  flags: FlagProps[];
  /** What the record is about, e.g. the vehicle; empty when unknown. */
  parameters: string[];
}

const toFlags = (...flags: (FlagProps | Nullish)[]) => flags.filter(isNotNil);

/**
 * What a linked record's tile shows. A record the viewer may not read (`detail` null)
 * is named by its kind alone.
 */
export const getCallLinkedRecordContent = (
  record: TelephonyCallDetailRecord
): CallLinkedRecordContent => {
  const kindLabel = i18n.t(`entity.customerMessage.labels.contextResource.${record.resourceId}`);
  const icon = RECORD_AUDIT_PAIR_RESOURCE_ICONS[record.resourceId];

  if (isNil(record.detail)) {
    return {kindLabel, icon, code: null, flags: [], parameters: []};
  }

  return match(record)
    .with({resourceId: 'INTEREST'}, (interest) => ({
      kindLabel,
      icon,
      code: interest.detail?.publicId ?? null,
      flags: toFlags(
        getInterestTypeFlagProps(interest.detail?.type),
        getInterestStateFlagProps(interest.detail?.state)
      ),
      parameters: [],
    }))
    .with({resourceId: 'BUSINESS_CASE'}, (businessCase) => ({
      kindLabel,
      icon,
      code: businessCase.detail?.publicId ?? null,
      flags: toFlags(
        getBusinessCaseTypeFlag(businessCase.detail?.type),
        getBusinessCaseStateFlag(businessCase.detail?.state)
      ),
      parameters: [],
    }))
    .with({resourceId: 'SERVICE_CASE'}, (serviceCase) => ({
      kindLabel,
      icon,
      code: serviceCase.detail?.number ?? null,
      flags: [],
      parameters: [serviceCase.detail?.vehicleTitle].filter(isNotNil),
    }))
    .with({resourceId: 'WORKSHOP_INTEREST'}, (workshopInterest) => ({
      kindLabel,
      icon,
      code: workshopInterest.detail?.publicId ?? null,
      flags: toFlags(getInterestStateFlagProps(workshopInterest.detail?.state)),
      parameters: [workshopInterest.detail?.vehicleTitle].filter(isNotNil),
    }))
    .exhaustive();
};
